/** A filing's compact path for any list that has the filing's id: the Home strip, a table cell.
 *
 * It reads the filing's own checks (the same read its page uses, so React Query shares the cache) and
 * draws the five stations as `FilingStepper`: five dots and the one station the filing is at, in words.
 * Until that read answers, or if it fails, it falls back to the three stations the verdict alone can
 * name (`stepsFromVerdict`) rather than drawing five it cannot vouch for, and the words still name the
 * station that is not done. **Nothing is ticked that the server did not say.**
 *
 * Home used a three-station path and the Filings list five, so one filing had two models; this is the
 * one drawing both use (design review 2026-11-03, dataviz #9).
 */

import { useFilingChecks } from "../../api/filingChecks";
import type { Scope2Method } from "../../api/queries";
import { FilingStepper } from "../../components/ui/FilingStepper";
import { stepsFromChecks, stepsFromVerdict } from "../filingSpine";

export function FilingPathCell({
  obligationId,
  method,
  verdict,
  blocking = 0,
  label,
  terse = false,
}: {
  obligationId: string;
  method: Scope2Method;
  /** The verdict a list row already holds, for the fallback. */
  verdict?: string | undefined;
  blocking?: number;
  /** For a screen reader: "Path, Vallecito Industries". */
  label: string;
  /** The station and its state only (Home's words budget). */
  terse?: boolean;
}) {
  const checks = useFilingChecks(obligationId, method);
  const steps = checks.data
    ? stepsFromChecks(checks.data.parts, checks.data.summary.verdict, checks.data.filing_state)
    : stepsFromVerdict(verdict, blocking);
  return <FilingStepper steps={steps} label={label} terse={terse} />;
}
